import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { getTemplateRouteLabel } from "utils/templates";

import { TeammatePicker } from "./TeammatePicker";
import { CLOSE_WITHOUT_SHARING_TOOLTIP } from "./utils";

import type { ShipmentTemplate } from "types/shipment";

interface Props {
  template: ShipmentTemplate;
  teammates: CompanyMember[];
  callerBranchId: number | null;
  // Null until the template's existing recipients have loaded.
  sharedRoleIds: number[] | null;
  loading: boolean;
  error: string | null;
  sharing: boolean;
  shareError: string | null;
  onShare: (recipientRoleIds: number[], onSuccess: VoidFunction) => void;
  onClose: VoidFunction;
}

export function ShareShipmentTemplateModal(props: Props) {
  const {
    template,
    teammates,
    callerBranchId,
    sharedRoleIds,
    loading,
    error,
    sharing,
    shareError,
    onShare,
    onClose,
  } = props;
  const routeLabel = getTemplateRouteLabel(template);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share template</DialogTitle>
      <Tooltip describeChild title={CLOSE_WITHOUT_SHARING_TOOLTIP}>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(1.5),
            right: theme.spacing(1.5),
          })}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        flexWrap="wrap"
        px={3}
        pb={1}
      >
        <Typography variant="body2" color="text.secondary">
          Sharing
        </Typography>
        <Typography variant="body2" fontWeight="fontWeightBold">
          {template.name}
        </Typography>
        {routeLabel && (
          <Typography variant="caption" color="text.secondary">
            ({routeLabel})
          </Typography>
        )}
      </Stack>
      <LoadingWrappedNode
        loading={loading}
        sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        {error !== null || sharedRoleIds === null ? (
          <DialogContent>
            <ErrorTypography error={error} />
          </DialogContent>
        ) : (
          <TeammatePicker
            teammates={teammates}
            callerBranchId={callerBranchId}
            sharedRoleIds={sharedRoleIds}
            sharing={sharing}
            error={shareError}
            onShare={onShare}
            onClose={onClose}
          />
        )}
      </LoadingWrappedNode>
    </Dialog>
  );
}
